import { useEffect, useState } from 'react'
import axios from 'axios'

/**
 * Centralised approved-lender list.
 *
 * Every lender dropdown / filter / explainer in the dashboard reads the ACTIVE
 * approved lenders from the case-delivery-app public endpoint
 * `POST /sentinel-data/approved-lenders`, which is backed by the
 * funder-monitoring `funder_defendants` table (active = 1). Nothing in the
 * dashboard should hardcode lender names any more — add or switch lenders in
 * Admin > Approved Lenders instead.
 */

export interface ApprovedLender {
    defendant: string
    category: number | string | null
}

export interface LenderOption {
    value: string
    label: string
}

/** Root of the case-delivery-app (REACT_APP_API_URL minus the trailing /api). */
export const caseDeliveryRootUrl = (): string => {
    const apiBase = process.env.REACT_APP_API_URL || ''
    return apiBase.replace(/\/api\/?$/, '')
}

export const APPROVED_LENDERS_URL = () => `${caseDeliveryRootUrl()}/sentinel-data/approved-lenders`

let cached: ApprovedLender[] | null = null
let inflight: Promise<ApprovedLender[]> | null = null

const normalise = (payload: any): ApprovedLender[] => {
    const rows: unknown[] = Array.isArray(payload?.data) ? payload.data : []
    return rows
        .map((row) => {
            if (typeof row === 'string') return { defendant: row, category: null }
            if (row && typeof row === 'object') {
                const defendant = (row as { defendant?: unknown }).defendant
                if (typeof defendant === 'string' && defendant.trim()) {
                    return { defendant: defendant.trim(), category: (row as ApprovedLender).category ?? null }
                }
            }
            return null
        })
        .filter((row): row is ApprovedLender => row !== null)
        .sort((a, b) => a.defendant.localeCompare(b.defendant))
}

/** Fetch (and memoise for the page lifetime) the active approved lenders. */
export const fetchApprovedLenders = async ({ force = false } = {}): Promise<ApprovedLender[]> => {
    if (cached && !force) return cached
    if (inflight && !force) return inflight
    inflight = axios
        .post(APPROVED_LENDERS_URL(), {})
        .then((response) => {
            cached = normalise(response.data)
            return cached
        })
        .finally(() => {
            inflight = null
        })
    return inflight
}

export const toLenderOptions = (lenders: ApprovedLender[]): LenderOption[] =>
    lenders.map(({ defendant }) => ({ value: defendant, label: defendant }))

interface UseApprovedLendersState {
    lenders: ApprovedLender[]
    options: LenderOption[]
    names: string[]
    loading: boolean
    error: string | null
    reload: () => Promise<void>
}

/** React hook over `fetchApprovedLenders` for dropdowns and filters. */
export const useApprovedLenders = (): UseApprovedLendersState => {
    const [lenders, setLenders] = useState<ApprovedLender[]>(cached ?? [])
    const [loading, setLoading] = useState<boolean>(!cached)
    const [error, setError] = useState<string | null>(null)

    const load = async (force = false) => {
        setLoading(true)
        setError(null)
        try {
            setLenders(await fetchApprovedLenders({ force }))
        } catch (err: any) {
            console.error('Unable to fetch approved lenders:', err)
            setError(err?.message || 'Could not load the approved lender list.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void load(false)
    }, [])

    return {
        lenders,
        options: toLenderOptions(lenders),
        names: lenders.map((l) => l.defendant),
        loading,
        error,
        reload: () => load(true),
    }
}
